import { useState } from "react";
import { Box, Avatar, Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CustomButton from "../../../component/elements/CustomButton/CustomButton";
import TextFieldCustom from "../../../component/elements/textfieldCustom";

export default function FeedbackCard({
  feedback,
  idx,
  handleReplySubmit,
  replyingIndex,
  setReplyingIndex,
  replyText,
  setReplyText,
}) {
  const [showReplies, setShowReplies] = useState(false);

  const bgColors = [
    "#A7C7E7", "#FFD6A5", "#FFB7B2", "#B5EAD7",
    "#C7CEEA", "#E2F0CB", "#FDCBBA", "#D5AAFF",
  ];

  const getDeterministicColor = (key) => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    return bgColors[Math.abs(hash) % bgColors.length];
  };

  /** Show only the first word (first name) in the UI. */
  const displayFirstWord = (name) => {
    if (!name || typeof name !== "string") return "?";
    const first = name.trim().split(/\s+/)[0];
    return first || "?";
  };

  const initials = feedback.name ? feedback.name.charAt(0).toUpperCase() : "?";
  const randomBg = getDeterministicColor(feedback.name || feedback.id || "default");

  return (
    <Box
      key={idx}
      className="flex flex-col bg-[#F9FDFF] p-4 rounded-xl shadow space-y-3 h-full flex-1"
    >
      {/* Header with avatar, name, date, stars */}
      <Box className="flex gap-3 items-center">
        <Avatar sx={{ bgcolor: randomBg }}>{initials}</Avatar>
        <Box className="flex flex-col">
          <div className="font-semibold text-gray-800 text-base">
            {displayFirstWord(feedback.name)}
          </div>
          <div className="text-sm text-gray-500">{feedback.date}</div>
          <Box className="flex mt-1">
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={i}
                fontSize="small"
                className={i < feedback.rating ? "text-yellow-400" : "text-gray-300"}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Comment bubble */}
      <Tooltip title={feedback.comment} arrow placement="right">
        <Box className="self-start bg-[#e6ebf1] text-gray-800 px-3 py-2 rounded-lg shadow-sm text-sm max-w-[90%]">
          {feedback.comment}
        </Box>
      </Tooltip>

      {/* Replies */}
      {feedback.replies?.length > 0 && (
        <div
          className="text-[#3587B8] font-[500] text-sm cursor-pointer"
          onClick={() => setShowReplies(!showReplies)}
        >
          {showReplies ? "Hide Replies" : `Show Replies (${feedback.replies.length})`}
        </div>
      )}

      {showReplies && feedback.replies.map((reply, i) => (
        <Box key={i} className="flex gap-2 items-start ml-6 mt-2">
          <Avatar sx={{ bgcolor: randomBg }}>
            {reply.name ? reply.name.charAt(0).toUpperCase() : "?"}
          </Avatar>
          <Box className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {displayFirstWord(reply.name)}
              </span>
              <span className="text-xs text-gray-500">{reply.date}</span>
            </div>
            <Box className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg shadow-sm text-xs max-w-[90%]">
              {reply.text}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Reply input or button - only if no replies yet */}
      {feedback.replies?.length === 0 && (
        replyingIndex === idx ? (
          <Box className="mt-2 space-y-2">
            <TextFieldCustom
              rows={1}
              fullWidth
              multiline
              heightClass="custom-textarea-height"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Add a reply..."
            />
            <div className="flex gap-2">
              <CustomButton
                onClick={() => handleReplySubmit(feedback.id)}
                customeClass="w-[70px] h-[30px] gradient-button"
              >
                Reply
              </CustomButton>
              <CustomButton
                onClick={() => setReplyingIndex(null)}
                customeClass="w-[70px] h-[30px] primary-custom-button"
              >
                Cancel
              </CustomButton>
            </div>
          </Box>
        ) : (
          <div
            className="text-[#3587B8] font-[500] text-sm cursor-pointer mt-2"
            onClick={() => setReplyingIndex(idx)}
          >
            Reply
          </div>
        )
      )}
    </Box>
  );
}
